import React from "react";
//#region types
import {
  AdvancedEditableTableCellFieldDefinition,
  AdvancedEditableTableRowProps,
  BaseFormFieldProps,
  WintFormKey,
} from "models/propTypes/wintFormPropsTypes";
import { IconDefinitionConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { TableRow, TableCell } from "@mui/material";
import Icon from "../../icon";
import RoundButton from "../../buttons/roundButton";
//#endregion
import { setIn } from "formik";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

function AdvancedEditableTableRow<T extends Record<string, any>>(props: AdvancedEditableTableRowProps<T>) {
  const {
    connect,
    name,
    index,
    row,
    fields,
    updateRow,
    removeRow,
    isLastRow,
    showRowIndex,
    hideActions,
    hideActionsCell,
  } = props;

  const rowName = `${name}[${index}]`;

  const setRowField = (name: WintFormKey<T>, value: any, shouldValidate?: boolean) => {
    const newRow = setIn(row, name, value);
    updateRow(index, newRow, shouldValidate);
  };

  const handleChange = (name: WintFormKey<T>) => (e: React.ChangeEvent<any>) => {
    setRowField(name, e.target.value);
  };

  const connectRowToForm = (name: WintFormKey<T>): BaseFormFieldProps => {
    const connector = connect((rowName + "." + name) as WintFormKey<any>);

    return {
      name: connector.name,
      value: connector.value,
      onChange: handleChange(name),
      onBlur: (e: React.FocusEvent<any>) => {
        connector.onBlur(e);
        connector.validate();
      },
      setValue: (value: any, shouldValidate?: boolean) => setRowField(name, value, shouldValidate),
      setTouched: (isTouched?: boolean, shouldValidate?: boolean) => connector.setTouched(isTouched, shouldValidate),
      touched: connector.touched,
      error: connector.error,
      hasError: connector.hasError,
    };
  };

  const resolveExtraProps = (
    field: AdvancedEditableTableCellFieldDefinition<T>,
    row: T,
    rowIndex: number,
    columnKey: keyof T
  ): Record<string, any> => {
    if (typeof field.extraProps === "function") {
      return field.extraProps(row, rowIndex, columnKey);
    }
    return field.extraProps ?? {};
  };

  const removeRowEvent = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    removeRow(index);
  };

  const onRowClick = React.useCallback(() => {
    if (isLastRow && !hideActions) {
      updateRow(index, { ...row }, index === 0);
    }
  }, [isLastRow, hideActions, index, row, updateRow]);

  return (
    <TableRow
      className={clsx(cssExports.fieldRow, isLastRow && !hideActions ? cssExports.tableLastRow : "")}
      onClick={onRowClick}
    >
      {showRowIndex ? <TableCell>{isLastRow && !hideActions ? "" : index + 1}</TableCell> : <></>}
      {Object.keys(fields).map((k) => {
        const field = fields[k]!;
        const columnKey = k as keyof T;
        const widthValue =
          field.cellWidth ??
          (field.widthColumnCount && typeof field.widthColumnCount === "number"
            ? `${(field.widthColumnCount / 12) * 100}%`
            : undefined);
        return (
          <TableCell
            key={`table-cell-${row[props.idField!] ?? index}-${k}`}
            align={field.alignment}
            className={field.className}
            style={{ width: widthValue, maxWidth: field.cellWidth }}
          >
            {React.createElement(field.Component, {
              ...connectRowToForm(k as WintFormKey<T>),
              ...resolveExtraProps(field, row, index, columnKey),
            })}
          </TableCell>
        );
      })}
      {!hideActionsCell && (
        <TableCell className={cssExports.editableTableActionButton}>
          {!isLastRow && !hideActions ? (
            <RoundButton
              size="xs"
              displayType={StyleEnums.DisplayType.None}
              className={cssExports.close}
              onClick={removeRowEvent}
            >
              <Icon icon={IconDefinitionConstants.WTimes} />
            </RoundButton>
          ) : (
            <></>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

export default AdvancedEditableTableRow;
